import COOKIE from 'cookick';
import { action, computed, observable } from 'mobx';
import APP from 'stores/APP';
import { ApiWebAccountInfoType, CloseStatus, CustomerAccountType, WebAccountInfoType } from 'types/customer';
import { AccountItemType, ApiWebUserDataType, WebUserDataType } from 'types/user';
import API from 'utils/API';
import http from 'utils/http';
import appStore from './APP';
import { chooseCurrentAccount, statusCodeMap } from './helper';

const SECURITY = 'TIGER_US_PORTAL_SECURITY';
const LOGIN_STATUS_KEY = 'isLogin'; // 记录在cookie中，用于状态同步，官网使用
const CLEARING_STATUS_KEY = 'isIBClearing'; // 记录在cookie中，状态同步，官网使用

const DOMAIN = window.location.hostname.split('.').slice(-2).join('.');

enum OtpStatus {
    unOpen,
    open
}

export class Security {
    @observable
    currentUser: WebUserDataType | null = null; // 用户状态

    // 当前账户的状态信息: 账户级别
    // 从/api/v1/web/account/info接口获取到的当前账户的账户信息, 区别于currentUser中的用户级别的信息
    // 只有customerId存在时, 此数据才有值
    @observable.shallow
    webAccountInfo?: WebAccountInfoType;

    // 只用来做currentUser为null时的bugsnag用户信息上报，其他地方不允许使用
    __bugsnagUser: Partial<WebUserDataType> | null = null;

    // 关户状态
    @observable
    closeStatus: CloseStatus = null;

    // 是否申请过销户
    @observable
    hasAppliedDelete: boolean = false;

    @action
    setCloseStatus(status: CloseStatus) {
        this.closeStatus = status;
    }

    @action
    setHasAppliedDelete(hasApplied) {
        this.hasAppliedDelete = hasApplied;
    }

    // 判断用户是否处于帐户关闭中或者帐户注销中
    @computed
    get isClosingOrDeleting() {
        return ['PENDING', 'REVOKING'].includes(this.closeStatus || '') || this.hasAppliedDelete;
    }

    /**
     * @description 判断用户是否为marsco帐户、默认为marsco帐户
     */
    @computed
    get isMarscoAccount() {
        return this.webAccountInfo?.clearing !== 'ib';
    }

    /**
     * @description 判断用户是否为 IRA 帐户
     */
    @computed
    get isIRAAccount() {
        return this.currentAccount?.category === 'IRA';
    }

    // 是否开启老虎令牌认证
    @observable
    optStatus: OtpStatus | null = null;

    @computed
    get isLogin() {
        return this.currentUser !== null;
    }

    ACCESS_TOKEN: string | null;

    // 获取当前账户
    @computed
    get currentAccount() {
        return this.currentUser?.currentAccount;
    }

    /**
     * @description 当前账户的账户类型
     */
    @computed
    get currentAccountType(): CustomerAccountType | 'virtual' | undefined {
        // customer服务中使用的大多数是ib/nd/bs, ib表示的就是fd账户
        const accountType = this.currentAccount?.accountType;

        return accountType === 'fd' ? 'ib' : accountType;
    }

    // 判断当前账户是否是环球账户
    @computed
    get isIB() {
        return !!this.currentAccountType && ['nd', 'ib'].includes(this.currentAccountType);
    }

    // 当前账户的账户状态, 包含开户/出入金/销户等状态
    @computed
    get currentProcessStatus() {
        // 只有没有customer_id的情况下processStatus才会没有值
        // 没有customer_id对应的用户状态是UNACCOUNT
        return this.webAccountInfo?.processStatus || 'UNACCOUNT';
    }

    // 用户状态映射而成的数字, 只有在状态先后比较的时候才需要使用, 与processStatus一一对应
    @computed
    get currentProcessStatusCode() {
        return this.webAccountInfo?.processStatusCode || 0;
    }

    // 邮箱/验证码等用户级别的信息
    @computed
    get userInfo() {
        return this.currentUser?.userInfo;
    }

    // 获取当前账户的资金账号
    @computed
    get currentAccountId() {
        return this.currentAccount?.accountId;
    }

    // 获得当前账户的customer_id
    @computed
    get currentCustomerId() {
        return this.currentAccount?.customerId;
    }

    // 获取所有的bs账户的列表
    @computed
    get bsAccountList() {
        return this.currentUser?.actualAccountInfo.accountList.filter(item => item.accountType === 'bs');
    }

    // 获取所有的ib(环球)账户的列表, 理论上最多只有一个ib账户
    @computed
    get ibAccountList() {
        return this.currentUser?.actualAccountInfo.accountList.filter(item =>
            ['fd', 'nd'].includes(item.accountType || '')
        );
    }

    // 获取所有的可以切换(有customer_id)的账户列表
    @computed
    get switchAccountList() {
        return this.currentUser?.actualAccountInfo.accountList.filter(item => !!item.customerId);
    }

    // marsco 账户在审核状态下可以入金
    @computed
    get isMarscoAuditing() {
        return this.isMarscoAccount && this.currentProcessStatus === 'AUDITING';
    }

    // 是否是开户状态（已开户|已入金）
    @computed
    get isAccountOpened() {
        return ['OPENED', 'FUNDED'].includes(this.currentProcessStatus);
    }

    @computed
    get missingBindEmailAndPhone() {
        if (this.userInfo) {
            const { email, phone } = this.userInfo;

            // 两个都不存在则为第三方用户
            return [email, phone].every(item => !item);
        }

        return false;
    }

    // 交易权限token
    @observable
    TRADE_TOKEN: string | null = null;

    @action
    async getTradeToken(params) {
        const { data } = await http.post(API.OAUTH.getTradeToken(), params);

        this.TRADE_TOKEN = data.trade_token;

        return data;
    }

    @action
    clearTradeToken() {
        this.TRADE_TOKEN = null;
    }

    // 设置tradeToken
    @action
    setTradeToken(tradeToken: string) {
        this.TRADE_TOKEN = tradeToken;
    }

    async ready() {
        try {
            // 是tradeup app的话，就通过TigerBridge尝试去获取token
            const TigerBridge = window.TigerBridge;

            // 获取token
            this.ACCESS_TOKEN = TigerBridge.getAccessToken();
            await this.getStatus();
        } catch (error) {
            const authLocalStorage = localStorage.getItem(SECURITY);
            const tokenFromCookie = COOKIE.getCookie(SECURITY);
            let tokenFromStorage = '';

            try {
                tokenFromStorage = authLocalStorage ? JSON.parse(authLocalStorage).token : '';
            } catch (err) {}

            /**
             * 若同时存在则判断两者是否相同
             * 不同则删除优先级低的localStorage的token，并将cookie中的token同步到localStorage中
             */
            if (tokenFromCookie && tokenFromStorage) {
                if (tokenFromCookie !== tokenFromStorage) {
                    localStorage.setItem(SECURITY, JSON.stringify({ token: tokenFromCookie }));
                }
            }

            // cookie中的token优先级最高，其次是localStorage中的，都不存在则为null
            const tempToken: null | string = tokenFromCookie || tokenFromStorage || null;

            // 如果tempToken存在，则证明已经登陆过，则获取用户状态信息
            if (tempToken) {
                this.ACCESS_TOKEN = tempToken;
                await this.getStatus();
            } else {
                this.currentUser = null;
                this.ACCESS_TOKEN = null;
            }
        }
    }

    async getStatus() {
        // 用户级别, 获取账户信息和通知信息
        const apiWebUserData = await http
            .get<ApiWebUserDataType>(API.OAUTH_USER.webUserStatus(), {
                params: {
                    // 注：app内打开webview时，os传 app，此时接口得到的 currentCustomerId 为APP中正在使用的账户，【特殊约定】
                    os: APP.isTiger ? 'app' : 'web'
                }
            })
            // accountList中过滤掉虚拟账户
            .then(({ data }) => ({
                ...data,
                actualAccountInfo: {
                    ...data.actualAccountInfo,
                    accountList: data.actualAccountInfo.accountList.filter(item => item.accountType !== 'virtual')
                }
            }));

        this.__bugsnagUser = apiWebUserData;

        const currentAccount = chooseCurrentAccount(apiWebUserData, APP.queryAccount);

        this.currentUser = {
            ...apiWebUserData,
            currentAccount
        };

        return this._fetchStatus(apiWebUserData, currentAccount);
    }

    async _fetchStatus(apiWebUserData: ApiWebUserDataType, currentAccount: AccountItemType) {
        const customerId = currentAccount.customerId;

        if (customerId) {
            // 获取当前账户的信息, 账户级别
            const accountInfo = await http
                .get<ApiWebAccountInfoType>(API.CUSTOMER.web.accountInfo(), {
                    params: { customer_id: customerId }
                })
                .then(res => res.data);
            const processStatus = accountInfo.status ? accountInfo.status.status : currentAccount.status;

            // 开户之后的用户状态accountInfo中拿不到(OPENED和FUNDED)
            // 所以, 如果accountInfo.status没有值的情况下, 取/web/user/status中当前账户的状态值
            this.webAccountInfo = {
                ...accountInfo,
                processStatus,
                processStatusCode: statusCodeMap[processStatus]
            };
        }

        // 注：不可删除下面逻辑，将账户状态同步到cookie中，官网Pricing会基于这个做判断。
        if (!this.isMarscoAccount) {
            setCookie(CLEARING_STATUS_KEY, 1);
        } else {
            delCookie(CLEARING_STATUS_KEY);
        }

        return apiWebUserData;
    }

    // 获取otp状态
    async getOtpStatus() {
        const { data } = await http.get<{ status: OtpStatus }>(API.OAUTH.otp.status());

        this.optStatus = data.status;
    }

    @action
    updateInviteCode(code: string) {
        if (this.currentUser) {
            this.currentUser.userInfo.inviteCode = code;
        }
    }

    // 发送短信
    sendSMS(params, platform = 'oauth', type = 'phone') {
        let sendMsgUrl = '';

        if (platform === 'portal') {
            // 个人这种线没有 portal 可以不使用
            // sendMsgUrl = API.PORTAL.sms();
        } else {
            sendMsgUrl = API.OAUTH[type === 'email' ? 'emailCode' : 'phoneCode']();
        }

        return http.post(sendMsgUrl, params, {
            withCredentials: true
        });
    }

    /**
     * 登录
     * @param {object} params 参数
     */
    login(config) {
        const { grant_type } = config;

        const data = {
            ...config,
            // 如果grant_type是phoneCode或emailCode，则将其转换为phone或email
            grant_type: grant_type.replace('Code', '')
        };

        return http
            .post(
                ['phoneCode', 'emailCode'].includes(grant_type) ? API.OAUTH.signinWithCode() : API.OAUTH.signin(),
                data,
                {
                    noToken: true,
                    withCredentials: true
                }
            )
            .then(resp => {
                // 将token和status存放到store
                this.ACCESS_TOKEN = resp.data.access_token;
                // 将token存储到localStorage和cookie
                saveUserLoginInfo(this.ACCESS_TOKEN);

                return resp;
            });
    }

    // 注销并清除token
    logout(isManual = true) {
        http.post(API.OAUTH.signout())
            .catch(err => {
                // ignore err
            })
            .finally(() => {
                this.clearAuth(isManual);
            });
    }

    // 清除token
    clearAuth(isManual = true) {
        localStorage.removeItem(SECURITY);
        delCookie(SECURITY);
        delCookie(LOGIN_STATUS_KEY);
        delCookie(CLEARING_STATUS_KEY);

        this.ACCESS_TOKEN = null;
        // 清除个人中心的登录状态
        this.currentUser = null;

        if (isManual) {
            // 主动退出清理deviceId
            appStore.resetDeviceId();
        }
    }
}

function setCookie(key: string, value: string | number) {
    COOKIE.setCookie(key, value, {
        path: '/',
        domain: DOMAIN,
        secure: !__DEV__
    });
}

function delCookie(key: string) {
    COOKIE.delCookie(key, { path: '/', domain: DOMAIN });
}

export default new Security();

/**
 * 将token存进cookie和localStorage
 * @param {string} token ACCESS_TOKEN信息
 */
export function saveUserLoginInfo(token) {
    setCookie(LOGIN_STATUS_KEY, 1);
    setCookie(SECURITY, token);

    localStorage.setItem(
        SECURITY,
        JSON.stringify({
            token
        })
    );
}
